import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ContentModerationService from "@/api/contentModerationService";

const defaultError = (err) => {
  const message =
    err?.response?.data?.message ||
    err?.message ||
    "An error occurred. Please try again.";
  toast.error(message);
};

const listKey = (section) => ["content-moderation-list", section];
const itemKey = (section, uuid) => ["content-moderation-item", section, uuid];

/**
 * Paginated moderation list of a section.
 * @param {string} section - groups | communities | activities | feedPosts
 * @param {Object} params - page, limit, search, status, date_from, date_to
 */
const useContentModerationList = (
  section,
  params = {},
  onError = defaultError,
) =>
  useQuery({
    queryKey: [...listKey(section), params],
    queryFn: () => ContentModerationService.getList(section, params),
    onError,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

/**
 * Basic details of one item (view page).
 */
const useContentModerationItem = (section, uuid, onError = defaultError) =>
  useQuery({
    queryKey: itemKey(section, uuid),
    queryFn: () => ContentModerationService.get(section, uuid),
    onError,
    retry: false,
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    enabled: !!uuid,
  });

const useModerationMutation = (
  section,
  mutationFn,
  onSuccess,
  onError = defaultError,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: listKey(section) });
      const uuid = typeof variables === "string" ? variables : variables?.uuid;
      if (uuid)
        queryClient.invalidateQueries({ queryKey: itemKey(section, uuid) });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/** variables: { uuid, reason? } */
const useDeactivateContent = (section, onSuccess, onError) =>
  useModerationMutation(
    section,
    (variables) => ContentModerationService.deactivate(section, variables),
    onSuccess,
    onError,
  );

/** variables: uuid */
const useReactivateContent = (section, onSuccess, onError) =>
  useModerationMutation(
    section,
    (uuid) => ContentModerationService.reactivate(section, uuid),
    onSuccess,
    onError,
  );

/** variables: uuid */
const useDeleteContent = (section, onSuccess, onError) =>
  useModerationMutation(
    section,
    (uuid) => ContentModerationService.delete(section, uuid),
    onSuccess,
    onError,
  );

export {
  useContentModerationList,
  useContentModerationItem,
  useDeactivateContent,
  useReactivateContent,
  useDeleteContent,
};
